import {
  Classes,
  Divider,
  FormGroup,
  InputGroup,
  NumericInput,
  TextArea,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { SystemMessage } from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { CopyToClipboardButton } from 'src/ui/components/CopyToClipboardButton';
import { Dialog } from 'src/ui/components/Dialog';
import { FormAction, FormActions } from 'src/ui/components/FormActions';
import {
  MANAGE_API_TOKEN_CREATE_COPY,
  MANAGE_API_TOKEN_CREATE_DIALOG,
  MANAGE_API_TOKEN_CREATE_ERROR,
  MANAGE_API_TOKEN_CREATE_EXPIRES_INPUT,
  MANAGE_API_TOKEN_CREATE_NAME_INPUT,
  MANAGE_API_TOKEN_CREATE_SECRET,
  MANAGE_API_TOKEN_CREATE_SHOWN_ONCE,
} from 'src/ui/constants/testIds';
import {
  API_TOKEN_CREATED,
  API_TOKEN_SECRET_COPIED,
} from 'src/ui/constants/trackingEventNames';
import { makeSubmitAction } from 'src/ui/containers/dialogs/makeSubmitAction';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useCreateApiTokenMutation,
  useTrackEventMutation,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';
import {
  GraphError,
  getErrorFromGraphQL,
} from 'src/ui/utils/getErrorFromGraphQL';

const DEFAULT_EXPIRES_DAYS = '90';
// Mirrors the server-side cap on token expiry.
const MAX_EXPIRES_DAYS = 360;

export const CreateApiTokenDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const onMutate = useOnMutate();
  const onError = useOnError();
  const queryClient = useQueryClient();
  const { mutate: trackEvent } = useTrackEventMutation(graphQLClient, {});

  const dialog = useAppSelector((state) => state.ui.dialog);
  let isOpen = false;
  let appId: string | undefined;
  if (dialog && dialog.type === DialogType.CREATE_API_TOKEN && dialog.data) {
    isOpen = true;
    appId = dialog.data.appId;
  }
  const kind = appId ? 'voicebox' : 'user';
  const dialogCopy = copy.components.manageApiTokens.createApiTokenDialog;

  const [name, setName] = React.useState('');
  const trimmedName = name.trim();
  const [expiresDaysInput, setExpiresDaysInput] = React.useState(
    DEFAULT_EXPIRES_DAYS
  );
  const [secret, setSecret] = React.useState('');
  const [error, setError] = React.useState('');

  // Counts every open and close. A request remembers the count from when it
  // was submitted, so a result that lands after the dialog has closed, or
  // has closed and reopened, is never shown in a dialog it did not come
  // from.
  const dialogSessionRef = React.useRef(0);
  React.useEffect(() => {
    dialogSessionRef.current += 1;
  }, [isOpen]);
  const dialogSession = dialogSessionRef.current;

  const expiresDays = Number(expiresDaysInput);
  const isExpiresValid =
    Number.isInteger(expiresDays) &&
    expiresDays >= 1 &&
    expiresDays <= MAX_EXPIRES_DAYS;

  const {
    isLoading,
    mutate: createApiToken,
    reset: resetCreate,
  } = useCreateApiTokenMutation(graphQLClient, {
    onMutate: () => {
      setError('');
      onMutate();
    },
    onError: (graphError: GraphError) => {
      const { button, message } = getErrorFromGraphQL(graphError);
      // Only the notification tray can render the action attached to an
      // error, so errors that carry one keep going there, as does any error
      // whose dialog is no longer the one on screen.
      if (button || dialogSessionRef.current !== dialogSession) {
        onError(graphError);
        return;
      }
      setError(message);
    },
    onSuccess: async (data, variables) => {
      if (!data.createApiToken?.secret) {
        return;
      }
      // Describe the token that was actually created, from the request that
      // created it, rather than whatever the dialog shows by the time the
      // response lands.
      const created = variables.input;
      // The secret is shown only in the dialog it was requested from, and
      // before anything else so the list refresh never delays it.
      if (dialogSessionRef.current === dialogSession) {
        setSecret(data.createApiToken.secret);
      }
      // The token now exists whether or not that dialog is still open.
      trackEvent({
        input: {
          event: API_TOKEN_CREATED,
          properties: JSON.stringify({
            kind: created.app_id ? 'voicebox' : 'user',
            default_expiry:
              created.expires_days === Number(DEFAULT_EXPIRES_DAYS),
          }),
        },
      });
      // A list request still in flight from before the create, including the
      // first load, would not include the new token, so stop it rather than
      // join it, then fetch again.
      await queryClient.cancelQueries(['listApiTokens']);
      queryClient.invalidateQueries(['listApiTokens']);
    },
  });

  // Clear the form however the dialog was closed, including by another
  // dialog replacing it, and drop the finished request with its secret.
  React.useEffect(() => {
    if (!isOpen) {
      setName('');
      setExpiresDaysInput(DEFAULT_EXPIRES_DAYS);
      setSecret('');
      setError('');
      resetCreate();
    }
  }, [isOpen, resetCreate]);

  const isSubmitDisabled = !trimmedName || !isExpiresValid || isLoading;

  const handleClose = () => {
    dispatch(closedDialog());
  };

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    createApiToken({
      input: {
        app_id: appId,
        expires_days: expiresDays,
        name: trimmedName,
      },
    });
  };

  const didCreate = Boolean(secret);

  const actions: FormAction[] = didCreate
    ? [
        {
          // The only action once the secret is shown, so it should look like
          // a button rather than text in the footer.
          minimal: false,
          text: dialogCopy.close,
          onClick: () => handleClose(),
        },
      ]
    : [
        {
          // The secret is shown only once, so the dialog stays open until the
          // request has either produced it or failed.
          disabled: isLoading,
          text: dialogCopy.cancel,
          onClick: () => handleClose(),
        },
        makeSubmitAction(dialogCopy.submit, isLoading),
      ];

  let submitTooltipText = '';
  if (!trimmedName) {
    submitTooltipText = dialogCopy.form.nameRequired;
  } else if (!expiresDaysInput.trim()) {
    submitTooltipText = dialogCopy.form.expiresRequired;
  } else if (!isExpiresValid) {
    submitTooltipText = dialogCopy.form.expiresRange(MAX_EXPIRES_DAYS);
  }

  return (
    <Dialog
      className="sd-dialog-create-api-token"
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      icon={IconNames.KEY}
      isCloseButtonShown={!isLoading}
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogCopy.title[kind]}
    >
      <div
        className={Classes.DIALOG_BODY}
        data-testid={MANAGE_API_TOKEN_CREATE_DIALOG}
      >
        {error ? (
          <SystemMessage
            testId={MANAGE_API_TOKEN_CREATE_ERROR}
            text={error}
            variant="alert"
          />
        ) : null}
        <form onSubmit={handleSubmit}>
          <FormGroup label={dialogCopy.form.name}>
            <InputGroup
              autoFocus
              data-testid={MANAGE_API_TOKEN_CREATE_NAME_INPUT}
              disabled={didCreate || isLoading}
              onChange={(evt) => setName(evt.target.value)}
              value={name}
            />
          </FormGroup>
          <FormGroup
            helperText={dialogCopy.form.expiresDaysHelp(MAX_EXPIRES_DAYS)}
            label={dialogCopy.form.expiresDays}
          >
            <NumericInput
              allowNumericCharactersOnly
              data-testid={MANAGE_API_TOKEN_CREATE_EXPIRES_INPUT}
              disabled={didCreate || isLoading}
              fill
              // Bounds the stepper arrows; typed values are validated instead of
              // silently clamped.
              max={MAX_EXPIRES_DAYS}
              min={1}
              minorStepSize={null}
              onValueChange={(_valueAsNumber, valueAsString) =>
                setExpiresDaysInput(valueAsString)
              }
              value={expiresDaysInput}
            />
          </FormGroup>

          <input type="submit" hidden />
        </form>
        {secret ? (
          <>
            <Divider />
            <FormGroup label={dialogCopy.form.token[kind]}>
              <div className="sd-create-api-token-secret">
                <TextArea
                  data-testid={MANAGE_API_TOKEN_CREATE_SECRET}
                  fill
                  growVertically
                  readOnly
                  value={secret}
                />
                <CopyToClipboardButton
                  onCopy={() =>
                    trackEvent({
                      input: {
                        event: API_TOKEN_SECRET_COPIED,
                        properties: JSON.stringify({ kind }),
                      },
                    })
                  }
                  testId={MANAGE_API_TOKEN_CREATE_COPY}
                  textToCopy={secret}
                />
              </div>
              <SystemMessage
                testId={MANAGE_API_TOKEN_CREATE_SHOWN_ONCE}
                text={dialogCopy.form.tokenHelp[kind]}
                variant="alert"
              />
            </FormGroup>
          </>
        ) : null}
      </div>
      <FormActions
        actions={actions}
        handleSubmit={handleSubmit}
        isSubmitDisabled={isSubmitDisabled}
        submitTooltipText={submitTooltipText}
      />
    </Dialog>
  );
};
